import { useState, type FormEvent, type ReactNode } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle, Field, Input, Stat, StatRow } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { CustomerPartner } from '../types';
import '../admin.css';

export interface PartnerPanelProps {
  partner: CustomerPartner | null | undefined;
  /** Billing staff may add, change or remove a company's partner, each with a reason. */
  canEdit: boolean;
  busy?: boolean;
  /** The API's sentence when a change was refused. */
  problem?: string | null;
  onSet: (code: string, reason: string) => void;
  onRemove: (reason: string) => void;
}

const sourceWord: Record<NonNullable<CustomerPartner['source']>, string> = {
  'sign-up': 'at sign-up',
  company: 'by the company',
  staff: 'by staff',
};

/**
 * The company's partner now (docs/partners.md) and its partner payments, 40% of which Ademero owes
 * each payment's partner, paid offline from Phone Home's partner report. Phone Home owns the partner
 * list, so the panel shows the code, never a name. A change counts for payments from then on.
 */
export function PartnerPanel({ partner, ...edit }: PartnerPanelProps): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Partner</CardTitle>
        {partner?.code ? <Badge tone="neutral">{partner.code}</Badge> : null}
      </CardHeader>
      {partner ? (
        <PartnerFacts partner={partner} />
      ) : (
        <p className="ad-small pui-muted">No partner.</p>
      )}
      {edit.canEdit ? <StaffForm current={partner?.code ?? null} {...edit} /> : null}
    </Card>
  );
}

/** The payments, how the partner came, and whose the latest payment was. */
function PartnerFacts({ partner }: { partner: CustomerPartner }): ReactNode {
  const f = useFormat();
  const linked =
    partner.code && partner.source && partner.linkedAt
      ? `Linked ${sourceWord[partner.source]} by ${partner.linkedBy ?? 'someone'}, ${f.date(partner.linkedAt)}.`
      : 'No partner now: payments from now on credit nobody. The payments above were made under earlier partners.';
  return (
    <>
      <StatRow>
        <Stat size="sm" value={f.int(partner.payments)} label="payments" />
        <Stat size="sm" value={f.money(partner.dollars)} label="collected" />
      </StatRow>
      <p className="ad-small" style={{ marginTop: 'var(--paige-space-3)' }}>
        {linked}
      </p>
      <CreditedElsewhere partner={partner} />
    </>
  );
}

/** The latest payment went to another code: a rename (the same partner) or an earlier partner of the company. */
function CreditedElsewhere({ partner }: { partner: CustomerPartner }): ReactNode {
  if (!partner.creditedCode || partner.creditedCode === partner.code) return null;
  return (
    <p className="ad-small pui-muted" role="status">
      The latest payment Phone Home has went to {partner.creditedCode}: the partner&apos;s code was
      renamed, or it was paid before the company changed to {partner.code}.
    </p>
  );
}

function StaffForm({
  current,
  busy,
  problem,
  onSet,
  onRemove,
}: Omit<PartnerPanelProps, 'partner' | 'canEdit'> & { current: string | null }): ReactNode {
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const idle = busy !== true && reason.trim() !== '';
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (idle && code.trim()) onSet(code.trim(), reason.trim());
  };
  return (
    <form onSubmit={submit} noValidate className="ad-stack">
      <TextField
        label={current ? 'New partner code' : 'Partner code'}
        value={code}
        max={32}
        onChange={setCode}
      />
      <TextField label="Reason" value={reason} max={500} onChange={setReason} />
      {problem ? (
        <p className="ad-small ad-danger" role="alert">
          {problem}
        </p>
      ) : null}
      <StaffButtons
        current={current}
        canSet={idle && code.trim() !== ''}
        canRemove={idle}
        busy={busy === true}
        onRemove={() => onRemove(reason.trim())}
      />
    </form>
  );
}

function StaffButtons(props: {
  current: string | null;
  canSet: boolean;
  canRemove: boolean;
  busy: boolean;
  onRemove: () => void;
}): ReactNode {
  return (
    <div className="ad-row">
      <Button type="submit" disabled={!props.canSet} loading={props.busy}>
        {props.current ? 'Change partner' : 'Add partner'}
      </Button>
      {props.current ? (
        <Button type="button" variant="ghost" disabled={!props.canRemove} onClick={props.onRemove}>
          Remove {props.current}
        </Button>
      ) : null}
    </div>
  );
}

function TextField(props: {
  label: string;
  value: string;
  max: number;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <Field label={props.label}>
      {({ id }) => (
        <Input
          id={id}
          value={props.value}
          maxLength={props.max}
          onChange={(e) => props.onChange(e.target.value)}
        />
      )}
    </Field>
  );
}
